import { Card, Text, TextBadge, VSpacer } from '@/components/DesignSystem';
import { getRemainingPctToHurdle } from '@/pages/CustomerDashboard/helpers';
import { ResolvedRewardsProgram, ResolvedTier } from '@/pages/CustomerDashboard/interfaces';
import { ProgramExclusionsDisclaimer } from '@/pages/CustomerDashboard/ProgramExclusionsDisclaimer';
import { buildRequirementTextForTier } from "@/utilities/RewardsProgramUtility";
import { ApiCategory, ApiUser } from '@api/interfaces';
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { alpha, LinearProgress, Stack, useTheme } from '@mui/material';
import { AccountGroupRole, CIBPageName, RewardsUom } from '@shared/enums';
import { formatShortCurrency, formatShortNumber } from '@shared/utilities';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AboutAccountRolesBanner } from '../LinkedAccounts';

interface TierProgressProps {
  accountGroupRole: AccountGroupRole | null,
  brandColor: string,
  categories: ApiCategory[],
  isReachedFinalTier: boolean,
  linkedAccounts: ApiUser[],
  nextTier: ResolvedTier,
  program: ResolvedRewardsProgram,
  rewards: ApiFarmerRewards,
}

export const TierProgress = ({
  accountGroupRole,
  brandColor,
  categories,
  isReachedFinalTier,
  linkedAccounts,
  nextTier,
  program,
  rewards,
}: TierProgressProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const howToReachNextTierText = useMemo(() => {
    return buildRequirementTextForTier(t, nextTier, isReachedFinalTier);
  }, [t, isReachedFinalTier, nextTier]);

  const hasExclusions = !!program.categoryIdsExcludedFromTierSpend.length
    || !!program.categoryIdsExcludedFromSegmentSpend.length;

  const filteredSegments = useMemo(() => {
    return nextTier.segments
      .filter(segment => !program.categoryIdsExcludedFromSegmentSpend.includes(segment.categoryId))
      .sort((a, b) => {
        const aRemaining = getRemainingPctToHurdle(a, rewards.totalPerSegment, nextTier);
        const bRemaining = getRemainingPctToHurdle(b, rewards.totalPerSegment, nextTier);
        if (aRemaining <= 0 && bRemaining <= 0) {
          const aCategory = categories.find((c) => c.id === a.categoryId)!;
          const bCategory = categories.find((c) => c.id === b.categoryId)!;
          return aCategory.name.localeCompare(bCategory.name);
        } else if (aRemaining <= 0 || bRemaining <= 0) {
          return aRemaining <= 0 ? 1 : -1;
        } else {
          return aRemaining - bRemaining;
        }
      });
  }, [categories, nextTier, rewards]);

  return (
      <Stack px="16px">
        {!!accountGroupRole && !!linkedAccounts?.length && (
          <AboutAccountRolesBanner
            brandColor={brandColor}
            customerRole={accountGroupRole}
            linkedAccounts={linkedAccounts}
            pageName={CIBPageName.CRD_CurrentProgress_TierProgress}
            showMobileVersion={true}
          />
        )}
        <VSpacer size="5" />
        <Text category="body-medium">
          {howToReachNextTierText}
        </Text>
        <VSpacer size="5" />
        {filteredSegments.map((segment) => {
          const { qualifyingTotalSpend, total } = rewards.totalPerSegment[segment.categoryId];
          const category = categories.find((c) => c.id === segment.categoryId)!;
          const segmentToHurdle = nextTier.segments.find(({ categoryId }) => (
            categoryId === segment.categoryId
          ))!;
          const remainingToHurdle = segmentToHurdle.minimumHurdle - total;
          const isNotYetStarted = total === 0;
          const isHurdleMet = remainingToHurdle <= 0;
          const { uom } = segmentToHurdle;
          const isDollar = uom === RewardsUom.Dollars;
          const remainingToHurdleValue = isDollar
            ? formatShortCurrency(remainingToHurdle, i18next)
            : `${formatShortNumber(remainingToHurdle, i18next)} ${uom}`;
          const remainingText = t($ => $.customerDashboard.tierProgress.remaining, {
            value: remainingToHurdleValue,
          });
          const goalValue = isDollar
            ? formatShortCurrency(segmentToHurdle.minimumHurdle, i18next)
            : `${formatShortNumber(segmentToHurdle.minimumHurdle, i18next)} ${uom}`;
          const goalText = t($ => $.customerDashboard.tierProgress.goal, {
            value: goalValue,
          });
          const barProgress = Math.min(total / segmentToHurdle.minimumHurdle * 100, 100);
          return (
            <>
              <Card
                elevation={0}
                key={segment.id}
                sx={{ backgroundColor: isHurdleMet
                  ? alpha(theme.palette.success.main, .12) : "#f5f5f5" }}
                testID={`${segment.id}-card`}
              >
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text category="title-medium">
                    {category.name}
                  </Text>
                  {isHurdleMet && (
                    <TextBadge
                      backgroundColor={theme.palette.success.main}
                      text={t($ => $.common.status.completed).toUpperCase()}
                    />
                  )}
                </Stack>
                <Text category="body-medium">
                  {t($ => $.customerDashboard.tierProgress.currentSpend, {
                    value: qualifyingTotalSpend ?? 0,
                  })}
                </Text>
                {(isNotYetStarted || isHurdleMet) ? (
                  <VSpacer size="3" />
                ) : (
                  <>
                    <Text category="title-small" textAlign="right">
                      {remainingText}
                    </Text>
                    <VSpacer size="2" />
                  </>
                )}
                <LinearProgress
                  sx={{
                    bgcolor: theme.palette.grey[400],
                    borderRadius: '8px',
                    '.MuiLinearProgress-bar1Determinate': {
                      bgcolor: isHurdleMet ? '#237804' : theme.palette.grey[900],
                    },
                    height: '6px',
                  }}
                  value={barProgress}
                  variant="determinate"
                />
                <VSpacer size="2"/>
                <Stack alignItems="end">
                  <Text category="body-medium">
                    {goalText}
                  </Text>
                </Stack>
              </Card>
              <VSpacer size="4" />
            </>
          );
        })}
        {hasExclusions && (
          <>
            <VSpacer size="3" />
            <ProgramExclusionsDisclaimer
              brandColor={brandColor}
              categories={categories}
              segmentSpendCategoryExclusionIds={program.categoryIdsExcludedFromSegmentSpend}
              tierSpendCategoryExclusionIds={program.categoryIdsExcludedFromTierSpend}
            />
            <VSpacer size="4" />
          </>
        )}
        <VSpacer size="9" />
      </Stack>
  );
};
